// Client half of the proof of work that guards the public contact and signup
// forms. Fetches a signed challenge from the API, hands it to a web worker to
// solve, and returns the fields to merge into the form submission.
//
// The solve costs a couple of seconds by design, so `onProgress` is called
// with reassuring copy once it starts to be noticeable -- without it the form
// just looks stuck.

// The first message shows early and deliberately: at a 2s delay it appeared
// just as the solve finished and flashed past unread. 400ms is long enough to
// stay quiet on a machine that finishes almost instantly, short enough that the
// text is readable for the whole remaining solve.
const FIRST_MESSAGE_DELAY_MS = 400;
const SECOND_MESSAGE_DELAY_MS = 8000;

const FIRST_MESSAGE = 'Verifying your browser — this takes a few seconds…';
const SECOND_MESSAGE = 'Still working, thanks for your patience…';

export async function solveProofOfWork(apiBaseUrl, scope, onProgress) {
    const notify = typeof onProgress === 'function' ? onProgress : () => {};

    const response = await fetch(`${apiBaseUrl}/pow-challenge?scope=${encodeURIComponent(scope)}`);
    if (!response.ok) {
        throw new Error('Could not reach the server. Please try again.');
    }
    const { challenge, bits } = await response.json();

    const timers = [
        setTimeout(() => notify(FIRST_MESSAGE), FIRST_MESSAGE_DELAY_MS),
        setTimeout(() => notify(SECOND_MESSAGE), SECOND_MESSAGE_DELAY_MS)
    ];

    let worker;
    try {
        const nonce = await new Promise((resolve, reject) => {
            worker = new Worker('/powWorker.js');

            worker.onmessage = (e) => {
                if (e.data && e.data.type === 'SOLVED') {
                    resolve(e.data.nonce);
                }
                // PROGRESS messages are only a liveness signal; the timers
                // above drive what the user actually sees.
            };

            worker.onerror = () => {
                reject(new Error('Could not verify your browser. Please try again.'));
            };

            worker.postMessage({ type: 'SOLVE', challenge, bits });
        });

        return { pow_challenge: challenge, pow_nonce: nonce };
    } finally {
        timers.forEach(clearTimeout);
        if (worker) {
            worker.terminate();
        }
        notify('');
    }
}
